import {Injectable} from '@angular/core';
import {HttpClient, HttpContext, HttpParams} from '@angular/common/http';
import {SKIP_GENERIC_ERROR_DIALOG} from '@app/shared/interceptors/http.error.interceptor';
import {Company, CompanyFeature, CompanyServerSide, UpdateCompanyFiscalYearDto} from 'app/models/company.model';
import {OrgChartUserDto} from 'app/models/user/org-chart-user.model';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {CreateDepartmentDto, Department} from '@app/models/department.model';
import {UpdateCompanyFeaturesDto} from '@app/models/company/update-company-features-dto';
import {LinkHrLockerCompanyDto} from '@app/models/company/link-hr-locker-company.dto';
import {CompanyService} from '@app/shared/api/interfaces/company/company.service';
import {Page} from '@app/models/api/page.model';
import {ApiUtils} from '@app/shared/utils/api.utils';
import {SortingParams} from '@app/models/api/sorting-params.model';
import {PagingParams} from '@app/models/api/paging-params.model';
import {CreateSiteDto, Site} from '@app/models/site.model';
import {UserMinimal} from '@app/models/user/user-minimal.model';
import {ProvisionedOrganisationalUnit} from '@models/ProvisionedOrganisationalUnit';
import {ProvisionedOfficeLocation} from '@models/ProvisionedOfficeLocation';
import {CustomUserFieldConfigDto} from '@app/admin/admin-modules/admin-company/admin-company-components/administrate-company-company-view/models/custom-user-field-config';

@Injectable()
export class CompanyAPIService implements CompanyService {

  private readonly BASE_URL = '/api/company';

  constructor(
      private http: HttpClient
  ) {
  }

  // #region - COMPANY
  getMyCompany() {
    const url = `${this.BASE_URL}/me`;
    return this.http.get<CompanyServerSide>(url).pipe(map(company => new Company(company)));
  }

  getById(id: number) {
    const url = `${this.BASE_URL}/${id}`;
    return this.http.get<CompanyServerSide>(url).pipe(map(company => new Company(company)));
  }

  delete(companyId: number): Observable<void> {
    const url = `${this.BASE_URL}/${companyId}`;
    return this.http.delete<void>(url);
  }
  // #endregion

  // #region - FEATURES
  updateFeatures(features: Array<CompanyFeature>): Observable<Array<CompanyFeature>> {
    const url = `${this.BASE_URL}/features`;
    return this.http.post<Array<CompanyFeature>>(url, features);
  }

  updateFeaturesForId(companyId: number, dto: UpdateCompanyFeaturesDto): Observable<Array<CompanyFeature>> {
    const url = `${this.BASE_URL}/${companyId}/features`;
    return this.http.post<Array<CompanyFeature>>(url, dto);
  }

  updateCustomUserFieldConfig(config: CustomUserFieldConfigDto) {
    const url = `${this.BASE_URL}/custom-field-title`;
    return this.http.put<CustomUserFieldConfigDto>(url, config);
  }
  // #endregion

  // #region - HR LOCKER
  // The backend endpoint is provisioner- and type-generic (see ExternalIdentitiesController's
  // /api/provisioners/{name}/external-identities/{type}/{companyId} routes and
  // ExternalCompanyMappingService) with hrlocker/company hardcoded as path segments here — its
  // request/response body field is externalCompanyId, remapped to/from this DTO's hrLockerCompanyId
  // at the edge so nothing above this service (component, model, spec files) needs to know the wire
  // shape changed.
  getLinkedHrLockerCompanyId(companyId: number): Observable<LinkHrLockerCompanyDto> {
    const url = `/api/provisioners/hrlocker/external-identities/company/${companyId}`;
    return this.http.get<{ externalCompanyId: string }>(url).pipe(
      map(response => ({ hrLockerCompanyId: response.externalCompanyId }))
    );
  }

  /**
   * SKIP_GENERIC_ERROR_DIALOG is set on this call only: the link modal already shows any failure
   * (a duplicate id or otherwise) inline itself via `confirmLink()`'s error handler, so the
   * interceptor's own generic error dialog would just double it up. Deliberately not set on
   * getLinkedHrLockerCompanyId/unlinkHrLockerCompany above/below, which have no inline handling of
   * their own and still rely on the generic dialog for a failure.
   */
  linkHrLockerCompany(companyId: number, dto: LinkHrLockerCompanyDto): Observable<void> {
    const url = `/api/provisioners/hrlocker/external-identities/company/${companyId}`;
    return this.http.post<void>(url, { externalCompanyId: dto.hrLockerCompanyId }, {
      context: new HttpContext().set(SKIP_GENERIC_ERROR_DIALOG, true)
    });
  }

  unlinkHrLockerCompany(companyId: number): Observable<void> {
    const url = `/api/provisioners/hrlocker/external-identities/company/${companyId}`;
    return this.http.delete<void>(url);
  }
  // #endregion

  // #region - DEPARTMENTS
  getDepartmentByType(type: string) {
    const url = `${this.BASE_URL}/department?type=${type}`; // TODO: Add queryParams as HttpParams - don't build into url string
    return this.http.get<Array<Department>>(url);
  }

  getAllDepartments() {
    return this.getDepartmentByType('Department');
  }

  searchDepartments(searchArgument: string, pagingParams: PagingParams, sortingParams: SortingParams): Observable<Page<Department>> {
    const url = `${this.BASE_URL}/department/search`;
    const params = ApiUtils.createPageAndSortParams(pagingParams, sortingParams)
      .append('searchArgument', searchArgument);

    return this.http.get<Page<Department>>(url, { params });
  }

  /**
   * Creates new department
   * @param createDepartmentDto
   */
  createDepartment(createDepartmentDto: CreateDepartmentDto): Observable<Department> {
    const url = `${this.BASE_URL}/department`;
    return this.http.post<Department>(url, createDepartmentDto);
  }

  /**
   * Updates existing department, provided it exists
   * @param departmentId
   * @param createDepartmentDto
   */
  updateDepartment(departmentId: number, createDepartmentDto: CreateDepartmentDto): Observable<Department> {
    const url = `${this.BASE_URL}/department/${departmentId}`;
    return this.http.put<Department>(url, createDepartmentDto);
  }

  /**
   * Deletes existing department, provided it exists and there is at least one remaining
   * @param departmentId
   */
  deleteDepartment(departmentId: number): Observable<Department> {
    const url = this.BASE_URL + `/department/${departmentId}`;
    return this.http.delete<Department>(url);
  }

  /**
   * Deletes multiple organizational units by id, provided they exist
   * @param departmentIds
   */
  deleteDepartmentsMultiple(departmentIds: number[]): Observable<Department[]> {
    const url = `${this.BASE_URL}/department/delete`;
    return this.http.post<Department[]>(url, departmentIds);
  }
  // #endregion

  // #region - SITES
  searchOfficeLocations(searchArgument: string, pagingParams: PagingParams, sortingParams: SortingParams): Observable<Page<Site>> {
    const url = `${this.BASE_URL}/site/search`;
    const params = ApiUtils.createPageAndSortParams(pagingParams, sortingParams)
      .append('searchArgument', searchArgument);
    return this.http.get<Page<Site>>(url, { params });
  }

  getAllSites() {
    const url = `${this.BASE_URL}/site`;
    return this.http.get<Array<Site>>(url);
  }

  /**
   * Creates an office location
   * @param createSiteDto
   */
  createSite(createSiteDto: CreateSiteDto): Observable<Site> {
    const url = `${this.BASE_URL}/site`;
    return this.http.post<Site>(url, createSiteDto);
  }

  /**
   * Updates an office location by id, provided it exists
   * @param createSiteDto
   */
  updateSite(siteId: number, createSiteDto: CreateSiteDto): Observable<Site> {
    const url = `${this.BASE_URL}/site/${siteId}`;
    return this.http.put<Site>(url, createSiteDto);
  }

  /**
   * Deletes an office location by id, provided it exists
   * @param officeLocation
   */
  deleteSite(id: number): Observable<Site> {
    const url = `${this.BASE_URL}/site/${id}`;
    return this.http.delete<Site>(url);
  }

  /**
   * Deletes multiple office locations by id, provided they exist
   * @param siteIds
   */
  deleteSitesMultiple(siteIds: number[]): Observable<Site[]> {
    const url = `${this.BASE_URL}/site/delete`;
    return this.http.post<Site[]>(url, siteIds);
  }
  // #endregion

  getCompanyOrgChart(): Observable<Array<OrgChartUserDto>> {
    const url = '/api/company/org-chart';
    return this.http.get<Array<OrgChartUserDto>>(url);
  }

  updateFiscalYear(updateCompanyFiscalYearDto: UpdateCompanyFiscalYearDto): Observable<Company> {
    const url = `${this.BASE_URL}/fiscal-year`;
    return this.http.put<CompanyServerSide>(url, updateCompanyFiscalYearDto).pipe(map(company => new Company(company)));
  }

  // updateCompanyWording(companyWording: CompanyWording): Observable<CompanyWording> {
  //   const url = `${this.BASE_URL}/company-wording`;
  //   return this.http.put<CompanyWording>(url, companyWording);
  // }

  getAllUsersAssignedToSite(siteId: number): Observable<UserMinimal[]> {
    const url = `${this.BASE_URL}/site/${siteId}/users`;
    return this.http.get<UserMinimal[]>(url);
  }

  getAllUsersAssignedToDepartment(departmentId: number): Observable<UserMinimal[]> {
    const url = `${this.BASE_URL}/department/${departmentId}/users`;
    return this.http.get<UserMinimal[]>(url);
  }

  getAllProvisionedOrganisationalUnits(): Observable<ProvisionedOrganisationalUnit[]> {
    const url = `${this.BASE_URL}/department/externally-provisioned`;
    return this.http.get<ProvisionedOrganisationalUnit[]>(url);
  }

  getProvisionedOrganisationalUnitByOrganisationalUnitId(organisationalUnitId: number): Observable<ProvisionedOrganisationalUnit> {
    const url = `${this.BASE_URL}/department/externally-provisioned/${organisationalUnitId}`;
    return this.http.get<ProvisionedOrganisationalUnit>(url);
  }

  getAllProvisionedOfficeLocations(): Observable<ProvisionedOfficeLocation[]> {
    const url = `${this.BASE_URL}/site/externally-provisioned`;
    return this.http.get<ProvisionedOfficeLocation[]>(url);
  }

  getProvisionedOfficeLocationByOfficeLocationId(officeLocationId: number): Observable<ProvisionedOfficeLocation> {
    const url = `${this.BASE_URL}/site/externally-provisioned/${officeLocationId}`;
    return this.http.get<ProvisionedOfficeLocation>(url);
  }

  getDepartments(ids: number[], name: string): Observable<Department[]> {
    const url = `${this.BASE_URL}/department/getbyid`;

    let params = new HttpParams();

    if (ids) {
      ids.forEach(id => {
        params = params.append('id', id);
      });
    }

    if (name && name.length > 0) {
      params = params.append('name', name);
    }

    return this.http.get<Department[]>(url, { params: params });
  }

  getSites(ids: number[], name: string): Observable<Site[]> {
    const url = `${this.BASE_URL}/site/getbyid`;

    let params = new HttpParams();

    if (ids) {
      ids.forEach(id => {
        params = params.append('id', id);
      });
    }

    if (name && name.length > 0) {
      params = params.append('name', name);
    }

    return this.http.get<Site[]>(url, { params: params });
  }
}
